import React from "react";
import {
  Stack,
  Fabric,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  IStackTokens,
  SearchBox,
  Text,
  IDetailsListProps,
  IDetailsRowStyles,
  DetailsRow,
  Callout,
  DirectionalHint,
  PrimaryButton,
  Breadcrumb,
} from "office-ui-fabric-react";
import { Loading } from "../../../services/Loading";
import PageTitle from "../../common/PageTitle";
import NoDataTile from "../../common/NoDataTile";
import { mdiDotsVertical } from "@mdi/js";
import Icon from "@mdi/react";
import {
  childrenGap,
  childrenGap8,
} from "../../common/Constants";
import CustomerIcon from "../../../images/customer.svg";
import CustomPagination from "../../common/CustomPagination";
import { ToastContext } from "../../common/ToastProvider";
import { IMergedCustomers } from "../../../vm";
import { deleteMergedCustomer, getMergedCustomers } from "../../../services/merged-customers/MergedCustomerService";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import ManageMergedCustomerDialog from "./ManageMergedCustomerDialog";
import { useHistory } from "react-router-dom";
import moment from "moment";

export interface MergedCustomersProps {}

const MergedCustomers: React.FC<MergedCustomersProps> = () => {
  // custom hooks
  const { showToast } = React.useContext(ToastContext);
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);
  const childrenGap24: IStackTokens = { childrenGap: 24 };
  const history = useHistory();

  // state
  const [isLoading, setLoading] = React.useState(false as boolean);
  const [customers, setCustomers] = React.useState([] as IMergedCustomers[]);
  const [count, setCount] = React.useState(0 as number);
  const [search, setSearch] = React.useState({
      offset: 0,
      limit: 12,
      search: ""
  } as {
     offset: number;
     limit: number;
     search: string;
  });
  const [manageDialog, setManageDialog] = React.useState({
      isOpen: false,
      index: undefined,
      data: undefined
  } as {
      isOpen: boolean;
      index?: number;
      data?: IMergedCustomers;
    })
  const [manageActionsCallout, setManageActionsCallout] = React.useState({
      isOpen: false,
      index: undefined,
      element: undefined,
      data: undefined,
  } as {
      isOpen: boolean;
      element: any;
      index?: number;
      data?: IMergedCustomers;
    });

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
        await getAllMergedCustomers();
    }
    asyncFunc();
  }, [])

  const getAllMergedCustomers = async (searchObject?: any) => {
      let searchObj = { ...search };
      if (searchObject) {
          searchObj = { ...searchObj, ...searchObject };
      }
      const result = await getMergedCustomers(searchObj);
      if (result?.success) {
          setCustomers(result.data.mergedCustomers);
          setCount(result.data.count);
          setSearch(searchObj);
      } else {
        showToast(result?.message || "Error while getting merged customers list", "error");
      }
  }

  const getCustomersFrom = async (from: number) => {
    const searchObj = { ...search, offset: from };
    await getAllMergedCustomers(searchObj);
  };

  const handleCallOutOpenClose = (
    isOpen: boolean,
    ele?: React.MouseEvent<HTMLSpanElement>,
    index?: number,
    data?: IMergedCustomers
  ) => {
      setManageActionsCallout({
        isOpen,
        element: isOpen ? ele?.currentTarget : undefined,
        index,
        data,
      })
  };

  const _renderItemColumn = (item: any, index: number, column: IColumn) => {
    switch (column?.key) {
      case "actions":
        return (
          <span
            onClick={(ele) => handleCallOutOpenClose(true, ele, index, item)}
          >
            <Icon
              path={mdiDotsVertical}
              size={0.9}
              className="pointer"
              color="#135c8a"
            />
          </span>
        );
      case "name":
        return (
          <Text styles={{ root: { fontWeight: 600 } }}>
            {item[column.fieldName as any]}
          </Text>
        );
      case "number":
        return (
          <Text styles={{ root: { fontWeight: 600 } }}>
            {search.offset + index + 1 + "."}
          </Text>
        );
      default:
        return <span>{item[column?.fieldName as any]}</span>;
    }
  };

  const _onRenderRow: IDetailsListProps["onRenderRow"] = (props) => {
    const customStyles: Partial<IDetailsRowStyles> = {};
    if (props) {
      if (props.itemIndex % 2 !== 0) {
        customStyles.root = { backgroundColor: "#F3F3F3" };
      }

      return <DetailsRow {...props} styles={customStyles} />;
    }
    return null;
  };

  const handleDialogOpen = (isOpen: boolean, index?: number) => {
    let mergedCustomers = undefined;
    if (index !== undefined) {
      mergedCustomers = { ...customers[index] };
    }

    setManageDialog({
      isOpen,
        index,
        data: mergedCustomers,  
    });
  };

  const handleDialogClose = (data: IMergedCustomers) => {
    let mergedCustomers = [...customers];
    if (data) {
      if (manageDialog.index !== undefined) {
        mergedCustomers[manageDialog.index] = data;
        showToast("Merged Customer updated successfully", "success");
      } else {
        mergedCustomers.unshift(data);
        showToast("Merged Customer added successfully", "success");
      }
    }
    setManageDialog({
        isOpen: false,
        index: undefined,
        data: undefined,
    })
    setCustomers(mergedCustomers);
  };

  const deleteCustomer = async (item: IMergedCustomers, index: number) => {
    showConfirmDialog(
      "Are You Sure",
      "Do you want to delete this",
      async () => {
          setLoading(true);
          setManageActionsCallout({
            isOpen: false,
            index: undefined,
            element: undefined,
            data: undefined,
          });    
        let result = await deleteMergedCustomer(item._id as string);
        if (result?.success) {
          showToast("Merged Customer deleted successfully", "success");
          let customerList = [...customers];
          customerList.splice(index, 1);
          setCustomers(customerList);
        } else {
          showToast(
            result.message || "Error while deleting merged customer",
            "error"
          );
        }
        setManageActionsCallout({
            isOpen: false,
            index: undefined,
            element: undefined,
            data: undefined,
          });
        setLoading(false);
      }
    );
  };

  return (
      <React.Fragment>
        {isLoading && <Loading isModeless={true} />}
        <Stack tokens={childrenGap24}>
          <Stack.Item>
            {/* <PageTitle
                icon={CustomerIcon}
                title="Merged Customers"
                description="List of all merged customers"
            /> */}
                <Breadcrumb
                    items={[
                    {
                        text: "Customers",
                        key: "customers",
                        onClick: () => {
                        history.push(`/customers?from=${0}&to=${12}&search=${""}&date=${moment()
                            .subtract(1, "month")
                            .format(
                            "MM/DD/YYYY"
                            )}&customerReportStatus=${10}&datacenterIds=${""}`);
                        },
                    },
                    {
                        text: "Merged Customers",
                        key: "merged-customers",
                        isCurrentItem: true,
                    },
                    ]}
                />
          </Stack.Item>
          <Stack.Item>
            <Stack horizontal horizontalAlign="space-between" wrap tokens={childrenGap}>
              <Stack.Item>
            <Stack horizontal wrap tokens={childrenGap}>
              <Stack.Item>
                <SearchBox
                  value={search.search}
                  styles={{ root: { width: "300px" } }}
                  placeholder="Customer Name"
                  onChange={async (e, value) =>
                    await getAllMergedCustomers({ ...search, search: value })
                  }
                />
              </Stack.Item>
            </Stack>
            </Stack.Item>
              <Stack.Item>
                  <PrimaryButton text="Merge" onClick={() => handleDialogOpen(true)} />
              </Stack.Item>
            </Stack>
          </Stack.Item>
          <Stack.Item>
            <div>
              {customers && customers.length > 0 ? (
                <Fabric>
                  <DetailsList
                    items={customers}
                    columns={[
                      {
                        key: "number",
                        name: "S. No",
                        fieldName: "number",
                        minWidth: 30,
                        maxWidth: 40,
                        isResizable: true,
                      },
                      {
                        key: "name",
                        name: "Name",
                        fieldName: "name",
                        minWidth: 200,
                        maxWidth: 350,
                        isResizable: true,
                      },
                      // {
                      //   key: "customerNames",
                      //   name: "Customers",
                      //   fieldName: "customerNames",
                      //   minWidth: 200,
                      //   maxWidth: 350,
                      //   isResizable: true,
                      // },
                      {
                        key: "datacenterNames",
                        name: "Locations",
                        fieldName: "datacenterNames",
                        minWidth: 350,
                        maxWidth: 600,
                        isResizable: true,
                      },
                      {
                        key: "actions",
                        name: "Actions",
                        fieldName: "actions",
                        minWidth: 80,
                        maxWidth: 110,
                        isResizable: true,
                      },
                    ]}
                    compact={true}
                    setKey="set"
                    onRenderItemColumn={_renderItemColumn as any}
                    layoutMode={DetailsListLayoutMode.justified}
                    selectionMode={SelectionMode.none}
                    onRenderRow={_onRenderRow}
                  />
                </Fabric>
              ) : (
                <NoDataTile title="No Merged Customers Found"></NoDataTile>
              )}
            </div>
          </Stack.Item>
          <Stack.Item>
            <CustomPagination
              name="Merged Customers"
              search={search}
              totalCount={count}
              getFrom={getCustomersFrom}
            />
          </Stack.Item>
        </Stack>
        {manageActionsCallout.isOpen && (
        <Callout
          styles={{ root: { width: 95, padding: 20 } }}
          role="alertdialog"
          ariaLabelledBy={
            manageActionsCallout && manageActionsCallout.data?._id
          }
          gapSpace={0}
          target={manageActionsCallout.element}
          directionalHint={DirectionalHint.leftCenter}
          onDismiss={() => handleCallOutOpenClose(false)}
        >
          <Stack tokens={childrenGap8}>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  handleDialogOpen(true, manageActionsCallout.index);
                }}
                className="callout-options"
              >
                Edit
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() => {
                  deleteCustomer(
                    manageActionsCallout.data as IMergedCustomers,
                    manageActionsCallout.index as number
                  );
                }}
                className="callout-options"
              >
                Delete
              </span>
            </Stack.Item>
            <Stack.Item className="pointer">
              <span
                onClick={() => history.push(`/customers/merged/${manageActionsCallout.data?._id}`)}
                className="callout-options"
              >
                View
              </span>
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {manageDialog.isOpen && (
          <ManageMergedCustomerDialog data={manageDialog.data} handleDialogClose={(data: IMergedCustomers) => handleDialogClose(data)} />
      )}
      </React.Fragment>
  )
};

export default MergedCustomers;
